/**
 * Copyright (c) 2026, CheckPlus Limited
 */

/**
 * English copy for the Plug marketing site, taken from `Plug Landing.dc.html`
 * in the "Plug marketplace CSR platform" Claude Design project.
 *
 * The section 7b corrections have been made (CP-318). What changed:
 *
 * - The platform-fee claims are gone. "NO CUT TAKEN" / "100% reaches the
 *   nonprofit" was false whatever the fee table: card processing is deducted
 *   before a gift arrives. The replacement quotes no rate at all, deliberately
 *   — checkplus-backend config.js carries two different tables (CheckPlus
 *   2.9% + HK$2.35 a card, Plug 3.4%), the Plug service fee is 0% only "at
 *   launch", and which applies to a marketplace gift is a product question. No
 *   number here can be right for long, so there is none.
 * - The business rule types are the real three: a share of revenue, a round-up
 *   on every sale, a fixed amount — with frequency chosen separately, and the
 *   connected-Shopify requirement stated on the two that need it.
 * - The demo figures read 0 until the directory has data, rather than claiming
 *   142 nonprofits, 384 businesses and HK$8.4M for a product that has not
 *   launched. The cause counts in data.js are zeroed to match.
 *
 * Still outstanding from 7b: charity sales are not mentioned on either the
 * business or the donor page, and the nonprofit proof tools are understated.
 * Both are additions rather than corrections and want product input.
 *
 * The dashboard figures further down (HK$142,800 received, 3,240 supporters)
 * are illustration inside a mocked-up panel, not claims about the platform,
 * and 7b does not list them.
 *
 * Every string is in this file and its two Chinese siblings, so a further
 * correction pass is a copy edit and touches no markup. A test pins the three
 * to identical key shapes, so a change has to land in all three.
 *
 * A later import from the same design (project 3d0c2fbb) took the four strings
 * it had actually reworded — the footer line, the FAQ lede, the heading above
 * the four value cards, and "giving rule" to "giving rules" on the business
 * card. It deliberately did not take the rest: the design's own handoff README
 * says the figures on it are pre-launch placeholders, and the 7b corrections
 * above are the answer to that, so where the two disagree this file wins.
 */

const copyEn = {
  documentTitle: "Plug",

  nav: {
    about: "What is Plug",
    donors: "For donors",
    business: "For businesses",
    nonprofit: "For nonprofits",
    login: "Log in",
    logoAlt: "Plug",
    beta: "BETA",
    cta: {
      about: "Sign up",
      donors: "Sign up",
      business: "Get started",
      nonprofit: "Apply to list",
    },
  },

  about: {
    pill: "One Ecosystem Connects Businesses, Nonprofits and Donors",
    h1: "Plug : a social impact ecosystem for a more giving society",
    lede:
      "Plug connects businesses, nonprofits and everyday donors in one place, so giving is more " +
      "convenient and sustainable. Businesses make giving part of everyday trade, donors support " +
      "causes in ways that suit them, and nonprofits gain more and steadier funding, greater " +
      "visibility and clearer ways to show their impact.",
    ctaPrimary: "Browse nonprofits",
    ctaSecondary: "See how it connects",

    flow: {
      kicker: "HOW PLUG CONNECTS AND SUPPORTS THE 3 SIDES",
      title: "One connected flow for giving. More value for every side.",
      business: {
        name: "Businesses",
        txt: "Set up and manage different types of giving rules easily on Plug.",
      },
      donors: {
        name: "Donors",
        txt: "Create giving plans easily, support one or more nonprofits, adjust the giving anytime.",
      },
      nonprofits: {
        name: "Nonprofits",
        txt:
          "Receive more and steadier funding from business giving rules and individual giving " +
          "plans, while showing supporters what their giving can help achieve.",
      },
      hub: "Verifies nonprofits, makes giving seamless, processes payments through Stripe, facilitates philanthropy",
      bizOut: "Give a share of sales revenue or more",
      bizBack: "CSR proof, automated admin, customer engagement",
      donorOut: "Recurring or one-time giving",
      donorBack: "Automatic processing, tax receipts, nonprofit updates",
      npDown: "More funding opportunities, simplified admin, brand awareness",
      npUp: "Proof of work and impact updates",
    },

    sides: [
      {
        id: "business",
        kicker: "WHAT BUSINESSES GET",
        title: "CSR that shows up on the shopping page.",
        txt:
          "Show your giving rules at checkout and on your public Plug profile, so shoppers can see " +
          "the causes their purchases support and even interact with it. Plug helps strengthen " +
          "customer engagement and purchase intent while processing donations and keeping your CSR " +
          "records in one place.",
        link: "Plug for businesses →",
      },
      {
        id: "donors",
        kicker: "WHAT DONORS GET",
        title: "Flexible giving, with everything kept in one place.",
        txt:
          "Create one or more giving plans, choose the amount and frequency, and split your giving " +
          "across the nonprofits you trust. Adjust your plans at any time, keep receipts in your " +
          "account, follow nonprofit updates and support businesses who give back to the society.",
        link: "Plug for donors →",
      },
      {
        id: "nonprofit",
        kicker: "WHAT NONPROFITS GET",
        title: "More funding, and less time spent asking for it.",
        txt:
          "Businesses and donors find you through Plug. The platform makes revenue-linked giving " +
          "practical for businesses and recurring giving convenient for individual donors. Receive " +
          "support through giving rules, giving plans and charity sales, while your public profile, " +
          "unit costs and impact updates help bring in the next supporter.",
        link: "Plug for nonprofits →",
      },
    ],

    valuesTitle: "The 4 things Plug is built on",
    values: [
      {
        title: "Simple to start. Easy to manage.",
        txt:
          "Set up giving rules and giving plans easily on Plug. Payment processing, splits and " +
          "receipt records are handled automatically.",
      },
      {
        title: "Built for sustained giving",
        txt:
          "Plug creates more CSR value from business giving via customer engagement and impact " +
          "proof, giving businesses a stronger reason to keep donating.",
      },
      {
        title: "Fairer access to support",
        txt:
          "Nonprofits of every size can be discovered on Plug, where the impact they share helps " +
          "potential donors understand and support their work.",
      },
      {
        title: "Verified end to end",
        txt:
          "Section 88 status is checked before listing, payments and payouts are handled through " +
          "Stripe, and no funds are held by Plug.",
      },
    ],
  },

  donors: {
    pill: "Anyone can give · 0 Section 88 nonprofits",
    h1: "Give a slice of everything you buy.",
    lede:
      "Set one amount, split it across as many Hong Kong nonprofits as you like, and Plug moves the " +
      "money in a single payment — with every receipt filed in one place. Start at HK$100 a month, " +
      "or give once.",
    ctaPrimary: "Start giving",
    ctaSecondary: "Browse nonprofits",
    note:
      "Free to join, no minimum, change or stop any month. Your receipt arrives as soon as a " +
      "payment succeeds.",
    shot: "APP SHOT · YOUR GIVING PLAN",

    press: { label: "AS SEEN IN", logo: "LOGO" },

    how: {
      kicker: "HOW IT WORKS",
      h2: "Three decisions, then it runs itself.",
      lede:
        "Most people finish setting up in under two minutes and never touch it again — though you " +
        "can change the split whenever you want.",
      steps: [
        {
          title: "Pick your causes",
          txt:
            "Browse by what nonprofits actually do — homelessness, hunger, disability, mental " +
            "health — and add the ones you trust.",
          shot: "SCREENSHOT · DISCOVER",
        },
        {
          title: "Set one total and divide it",
          txt:
            "Drag a weight and the others rebalance, so your split always adds to 100%. One " +
            "nonprofit? No percentages at all.",
          shot: "SCREENSHOT · SPLIT",
        },
        {
          title: "Get a receipt on the spot",
          txt:
            "Your Section 88 receipt is issued the moment a payment succeeds — often within " +
            "seconds — alongside an impact report showing what your money bought.",
          shot: "SCREENSHOT · IMPACT",
        },
      ],
    },

    calc: {
      kicker: "TRY IT — NO ACCOUNT NEEDED",
      h2: "What would your giving buy?",
      lede: "Real unit costs published by three nonprofits on Plug, split the way most new givers split it.",
      label: "I COULD GIVE",
      currency: "HK$",
      per: "/ month",
      footBefore: "That is ",
      footAfter: " over a year, divided across three nonprofits.",
      yearly: "a year",
      photo: "PHOTO PLACEHOLDER",
    },

    onePayment: {
      kicker: "ONE PAYMENT, ONE PLACE",
      h2: "Support multiple causes at once. Keep every receipt in one place.",
      lede:
        "Nobody wants a separate standing instruction, login and PDF for every organisation they " +
        "support. Plug collapses all of it into a single payment and a single record.",
      give: {
        title: "Give to many, pay once",
        txt:
          "Add as many nonprofits as you like to one plan. Your total is charged as a single " +
          "payment, then divided by the weights you set — no separate transactions, no repeating " +
          "your card details for each one.",
        oneLabel: "One payment",
        oneValue: "HK$400 / month",
        checks: [
          "Weights always total 100% — move one, the rest rebalance",
          "Add or drop a nonprofit without touching your total",
        ],
      },
      receipts: {
        title: "Every receipt, one shelf",
        txt:
          "Each successful payment produces one Section 88 receipt covering every nonprofit in it — " +
          "filed in your account the moment it clears. At tax time you download the year, not a " +
          "folder of emails.",
        label: "YOUR RECEIPTS · 2026",
        note: "Section 88 eligible",
        pdf: "PDF",
        totalLabel: "Year to date",
        totalValue: "HK$3,200",
        rows: [
          { date: "1 Aug 2026", detail: "RC-2608 · 4 nonprofits", amount: "HK$400" },
          { date: "1 Jul 2026", detail: "RC-2607 · 4 nonprofits", amount: "HK$400" },
          { date: "12 Jun 2026", detail: "RC-2606b · one-time, 1 nonprofit", amount: "HK$1,000" },
          { date: "1 Jun 2026", detail: "RC-2606 · 3 nonprofits", amount: "HK$400" },
        ],
        checks: [
          "Issued on successful payment, often within seconds",
          "Download a whole year as one file for your tax return",
        ],
      },
    },

    marketplace: {
      kicker: "THE MARKETPLACE",
      h2: "0 nonprofits, sorted by what they do.",
      lede:
        "Every organisation is verified against the IRD's Section 88 list, with audited accounts " +
        "and published unit costs on its profile.",
      browse: "Browse all nonprofits",
      countSuffix: " nonprofits",
    },

    impact: {
      kicker: "IMPACT THIS YEAR",
      h2: "Ordinary spending, turned into shelter, meals and care.",
      lede: "Reported by the nonprofits themselves, against the unit costs published on their profiles.",
      stats: [
        { num: "21,400", txt: "nights of shelter in Sham Shui Po and Yau Ma Tei" },
        { num: "468,000", txt: "meals rescued and delivered to households" },
        { num: "3,100", txt: "subsidised counselling hours, no waiting list over two weeks" },
        { num: "640", txt: "disabled adults placed into open employment" },
      ],
      quote: {
        txt:
          "“I set HK$200 a month across three organisations and forgot about it. Eleven months " +
          "later there's a page telling me it paid for eight nights of shelter and a hundred and " +
          "sixty meals.”",
        mono: "TL",
        name: "Tania L.",
        meta: "Giving since October 2025 · Kennedy Town",
        photo: "PHOTO",
      },
    },

    trust: [
      {
        kicker: "WHAT IT COSTS",
        title: "Free to join. Free to list.",
        txt: "Fees only ever come out of a gift that actually arrives — never a monthly bill for being here.",
      },
      {
        kicker: "VERIFIED, NOT LISTED",
        title: "Section 88 checked with the IRD",
        txt:
          "Charity number, audited accounts and programme costs are confirmed before a nonprofit " +
          "can receive a single dollar.",
      },
      {
        kicker: "MONEY MOVEMENT",
        title: "Settled through Stripe",
        txt:
          "Funds never sit with us. Receipts are issued on successful payment, and payouts batch " +
          "into one clean transfer for the nonprofit.",
      },
    ],

    cross: [
      {
        id: "business",
        kicker: "RUN A BUSINESS?",
        title: "Give a share of revenue, automatically.",
        txt: "Publish a giving rule, show it on your storefront, and get the CSR report without building one.",
        link: "See Plug for businesses →",
      },
      {
        id: "nonprofit",
        kicker: "ARE YOU A NONPROFIT?",
        title: "Recurring support, fully accounted for.",
        txt: "List with your Section 88 number and see exactly which individuals and businesses funded you.",
        link: "See Plug for nonprofits →",
      },
    ],
  },

  business: {
    pill: "0 Hong Kong businesses giving on Plug",
    h1: "Write one giving rule. Let your revenue do the rest.",
    lede:
      "Commit a share of revenue, a round-up on every sale, or a fixed amount. Plug reads your " +
      "Stripe settlements, respects your cap, splits it across the nonprofits you pick, and issues " +
      "a receipt on every successful payment.",
    ctaPrimary: "Publish a giving rule",
    ctaSecondary: "Talk to our team",
    note: "Nothing to publish a rule. We verify your Business Registration before it goes public.",
    shot: "DASHBOARD SHOT · GIVING RULE",

    rules: {
      kicker: "CHOOSE YOUR RULE",
      h2: "Three ways to commit, one way to report.",
      lede: "Switch between them whenever your books change, and choose separately how often the rule runs.",
      cards: [
        {
          tag: "MOST COMMON",
          headline: "1–3%",
          title: "A share of monthly revenue",
          txt:
            "Calculated from your settled revenue, so giving scales with a good month and eases " +
            "off in a slow one.",
          note: "Needs a connected Shopify store. Set a cap and it never exceeds it.",
        },
        {
          tag: "CUSTOMER FACING",
          headline: "0.5–5%",
          title: "A round-up on every sale",
          txt:
            "Each sale is rounded up to the nearest dollar and the difference is given, so " +
            "customers see what their purchase triggered — paid by you, not by them.",
          note: "Needs a connected Shopify store. Never a surcharge on the customer.",
        },
        {
          tag: "PREDICTABLE",
          headline: "Flat",
          title: "A fixed amount",
          txt:
            "One agreed amount, whatever revenue does — the simplest line for finance to plan " +
            "around. How often it goes out is yours to choose.",
          note: "Monthly, or a one-off for a campaign.",
        },
      ],
    },

    customers: {
      kicker: "WHAT YOUR CUSTOMERS SEE",
      h2: "Giving you can point at, not just claim.",
      lede:
        "Your rule appears as a badge on your storefront, a footnote on the receipt, and a public " +
        "page listing the nonprofits you fund and how much has reached them. The giving is yours — " +
        "customers are told what their purchase triggered, never charged for it.",
      checks: [
        "A storefront badge and receipt footnote, in three languages",
        "Let staff nominate a nonprofit into the split each quarter",
        "A CSR report your auditor accepts as-is",
      ],
      badgeTitle: "Storefront badge",
      badgeTxt: "“We give 2% of every sale to 3 Hong Kong nonprofits”",
      receiptLabel: "RECEIPT FOOTNOTE",
      subtotalLabel: "Subtotal",
      subtotalValue: "HK$248.00",
      totalLabel: "Total paid",
      totalValue: "HK$248.00",
      receiptFootBefore: "Thanks — this purchase means ",
      receiptFootWe: "we",
      receiptFootMid: " give ",
      receiptFootAmount: "HK$4.96",
      receiptFootAfter: " to 3 Hong Kong nonprofits. Nothing was added to your bill.",
      photo: "PHOTO · SHOPFRONT WITH BADGE",
    },

    directory: {
      kicker: "IN GOOD COMPANY",
      h2: "Businesses already giving through Plug.",
      link: "See the full directory →",
      rows: [
        { name: "Lung Cheong Bakery", industry: "Food & drink", district: "Sham Shui Po", rule: "2% of revenue" },
        { name: "Island Dental Studio", industry: "Health", district: "Central", rule: "HK$10,000 / month" },
        { name: "Kwun Tong Print Co.", industry: "Services", district: "Kwun Tong", rule: "2.5% of revenue" },
        {
          name: "Kadoorie Coffee Roasters",
          industry: "Food & drink",
          district: "Yau Ma Tei",
          rule: "1.5% of every sale",
        },
      ],
    },

    quote: {
      txt:
        "“We used to pick a charity each Christmas and forget the rest of the year. Now 2% of " +
        "revenue moves every month and the report writes itself — our customers ask about it at " +
        "the counter.”",
      mono: "LC",
      name: "Owner",
      meta: "Lung Cheong Bakery · Sham Shui Po · giving since 2021",
      photo: "PHOTO",
    },
  },

  nonprofit: {
    pill: "Section 88 registered organisations only",
    h1: "Recurring support, fully accounted for.",
    ledeParts: [
      "List your organisation and you become a choice for numerous individuals and businesses who " +
        "give through Plug. Funds arrive as one clean transfer with a line-by-line statement — " +
        "seamless and convenient.",
      "We are here to help nonprofits raise more, so certified nonprofit organisations can be " +
        "listed on Plug completely FREE of charge!",
    ],
    ctaPrimary: "Apply to list",
    ctaSecondary: "Download the one-pager",
    note: "Free to list, and nothing monthly. Your statement shows the gross and the net side by side.",
    shot: "DASHBOARD SHOT · WHERE IT COMES FROM",

    why: {
      kicker: "WHY LIST ON PLUG",
      h2: "More ways to fund your mission.",
      cards: [
        {
          id: "storefront",
          title: "Seamless donations from businesses",
          txt:
            "Shops and firms commit a share of their revenue through Plug. When their trade grows, " +
            "so does your funding, with no ask required.",
        },
        {
          id: "heart",
          title: "Recurring donations from individual donors",
          txt:
            "Donors set an amount once and it keeps giving until they change it, so support " +
            "arrives without a new campaign each time.",
        },
        {
          id: "people",
          title: "Greater visibility for charity collaborations",
          txt:
            "Your mission page, updates and unit costs sit in one place, so businesses and donors " +
            "can find you and start a collaboration.",
        },
        {
          id: "coin",
          title: "No onboarding fee, no monthly fee",
          txt:
            "Listing and running your page costs nothing. Plug only takes a platform fee on " +
            "donations you actually receive.",
        },
      ],
    },

    dashboard: {
      kicker: "YOUR DASHBOARD",
      h2: "See exactly who funded you, and how.",
      lede:
        "Recurring versus one-time. Individuals versus businesses. Which giving rule sent what, " +
        "month by month — and which supporters stopped, so you know before your budget does.",
      checks: [
        "One Stripe transfer with a statement your auditor accepts as-is",
        "Publish unit costs so givers see what HK$120 actually buys",
        "Post programme updates that appear in every supporter's impact report",
      ],
      panelLabel: "RECEIVED THIS MONTH",
      panelValue: "HK$142,800",
      individuals: { name: "Individuals", meta: "3,240 people giving a slice of their spending", pct: "62%" },
      businesses: { name: "Businesses", meta: "84 with a published giving rule", pct: "38%" },
      stats: [
        { num: "+186", txt: "new supporters this month" },
        { num: "HK$96,400", txt: "recurring, so predictable" },
      ],
    },

    checkplus: {
      pill: "DEEP CHECKPLUS INTEGRATION",
      h2: "Connect CheckPlus and the whole back office runs itself.",
      lede:
        "Plug is built by the CheckPlus team, so the two are wired together properly. Connect your " +
        "account and Plug uses your receipt templates, reports, donation page and campaigns as they " +
        "are. No exports, no re-keying.",
      link: "See the CheckPlus integration →",
      features: [
        {
          title: "Automatic receipts",
          txt: "Section 88 receipts on your own template, issued on every successful payment. Nobody presses send.",
        },
        {
          title: "Accounting reports",
          txt: "Ledger-ready reports in the format your auditor already accepts. Nothing special to do at year end.",
        },
        {
          title: "Full payment data",
          txt:
            "Supporter, method, split and settlement reference on every transaction — one transfer, " +
            "traced to each gift.",
        },
        {
          title: "Your own donation page",
          txt:
            "A branded page for giving outside the marketplace. Share it by link or QR code; " +
            "receipting is automatic.",
        },
        {
          title: "Campaigns with goals",
          txt: "Set a target and a deadline, watch the progress bar. Tracked separately, paid out together.",
        },
        {
          title: "Live supporter records",
          txt: "Givers arrive as contacts, recurring plans as pledges, and they stay up to date on their own.",
        },
        {
          title: "One login for both",
          txt: "Sign into CheckPlus with your Plug credentials. Same account, same team, no second password.",
        },
      ],
      stripName: "CheckPlus",
      stripTxt:
        "One login for both. Your Plug credentials sign you into CheckPlus — no second account to " +
        "create, no separate password. Not on CheckPlus yet? We set it up when you list, since " +
        "Stripe verification runs through it.",
      stripCta: "Connect an account",
    },

    join: {
      kicker: "HOW TO JOIN",
      h2: "Listed in about a week.",
      lede: "Most of that week is us checking your paperwork, not you filling in forms.",
      steps: [
        {
          n: "1",
          title: "Apply with your Section 88 number",
          txt:
            "Charity number, latest audited accounts and a bank account in the organisation's " +
            "name. Ten minutes of form.",
        },
        {
          n: "2",
          title: "Verify with Stripe via CheckPlus",
          txt:
            "We check your number against the IRD list. Stripe verification runs through CheckPlus " +
            "in the same flow — new to it or not.",
        },
        {
          n: "3",
          title: "Publish your unit costs",
          txt:
            "Tell givers what HK$120 or HK$800 actually buys. These are the numbers that appear in " +
            "every impact report.",
        },
      ],
      eligibility:
        "Eligibility: your organisation must be a charity registered in Hong Kong and exempt from " +
        "tax under Section 88 of the Inland Revenue Ordinance. We verify the number against the IRD " +
        "list, along with your most recent audited accounts.",
      eligibilityCta: "Check eligibility",
    },

    quote: {
      txt:
        "“Plug replaced our whole donations admin. Eighty-four businesses and three thousand " +
        "individuals arrive as one transfer, with a statement our auditor accepts as-is.”",
      mono: "HS",
      name: "Finance lead",
      meta: "Harbour Shelter Trust · Sham Shui Po",
      photo: "PHOTO",
    },
  },

  causes: {
    homeless: {
      label: "Homelessness",
      blurb: "Shelter, street outreach and case work towards permanent tenancies.",
    },
    poverty: {
      label: "Poverty & Hunger",
      blurb: "Food rescue, canteens and cash assistance for low-income households.",
    },
    disability: {
      label: "Disability",
      blurb: "Independent living, job coaching and interpreting, free at the point of need.",
    },
    youth: {
      label: "Youth & Education",
      blurb: "Study space, tutoring and paid apprenticeships for school leavers.",
    },
    mental: {
      label: "Mental Health",
      blurb: "Sliding-scale therapy and a 24-hour crisis line in four languages.",
    },
    elderly: {
      label: "Elderly Care",
      blurb: "Home visits, hot meals and companionship for people living alone.",
    },
    environment: {
      label: "Environment",
      blurb: "Shoreline restoration, waste reduction and urban biodiversity.",
    },
    animals: {
      label: "Animal Welfare",
      blurb: "Rescue, veterinary care and rehoming for abandoned animals.",
    },
  },

  outcomes: {
    shelter: { name: "Harbour Shelter Trust", one: "night of safe shelter", many: "nights of safe shelter" },
    food: { name: "Kowloon Food Rescue", one: "rescued meal delivered", many: "rescued meals delivered" },
    counselling: {
      name: "Quiet Harbour Counselling",
      one: "subsidised therapy hour",
      many: "subsidised therapy hours",
    },
    brightSteps: { name: "Bright Steps Network" },
  },

  faqs: {
    donors: [
      {
        q: "Any onboarding or subscription fee for nonprofits?",
        a:
          "No. There is no onboarding fee and no subscription fee. As long as an organisation is " +
          "Section 88 certified, it can be onboarded on Plug free of charge.",
      },
      {
        q: "Can I give to more than one nonprofit at once?",
        a:
          "That is the point. Set one total, add as many nonprofits as you like, and drag the " +
          "weights — the others rebalance so your split always adds to 100%. With a single " +
          "nonprofit there are no percentages at all.",
      },
      {
        q: "When do I get my receipt?",
        a:
          "As soon as a payment succeeds — often within seconds of the transaction. Each receipt is " +
          "Section 88 eligible and covers every nonprofit in that payment, so a split across four " +
          "organisations still arrives as one document.",
      },
      {
        q: "Can I give once instead of every month?",
        a:
          "Yes. Choose one-time and it is charged once, with the receipt issued immediately. " +
          "Recurring can be monthly, quarterly or once a year.",
      },
      {
        q: "What if I want to stop or change the split?",
        a:
          "Change the amount, re-weight the split, add or remove nonprofits, or pause entirely — " +
          "any time, with no minimum period and no cancellation step to hunt for.",
      },
    ],

    business: [
      {
        q: "How is our rule calculated?",
        a:
          "From your Stripe settlements. Choose a share of revenue, a round-up on every sale, or " +
          "a fixed amount, choose how often it runs, set a cap if you want one, and Plug does the " +
          "arithmetic and the filing.",
      },
      {
        q: "What does it cost us?",
        a:
          "Nothing to publish a rule and nothing monthly. What you pay is what your rule " +
          "commits, plus the processing on each gift it sends.",
      },
      {
        q: "Can we change the rule later?",
        a:
          "Yes. Switch type, adjust the percentage, move the cap or re-weight the nonprofits " +
          "whenever your books change. The new rule takes effect the following month.",
      },
      {
        q: "Can staff choose the nonprofits?",
        a:
          "Turn on staff nominations and your team votes one organisation into the split each " +
          "quarter. It stays inside the same total, so nothing about your commitment changes.",
      },
      {
        q: "What do we get for CSR reporting?",
        a:
          "A monthly statement, a receipt on every successful payment, and a shareable impact " +
          "report showing the nonprofits funded and what the money bought.",
      },
    ],

    nonprofit: [
      {
        q: "Who is eligible to list?",
        a:
          "Charities registered in Hong Kong and exempt from tax under Section 88 of the Inland " +
          "Revenue Ordinance. We verify the number against the IRD list, along with your most " +
          "recent audited accounts.",
      },
      {
        q: "What does listing cost?",
        a:
          "Nothing to join and nothing monthly. Card processing is deducted by the payment " +
          "provider before a gift reaches you; your statement shows the gross and the net side " +
          "by side.",
      },
      {
        q: "How and when do we get paid?",
        a:
          "Contributions batch into one Stripe transfer with a line-by-line statement, so you " +
          "reconcile a single deposit rather than thousands of small ones.",
      },
      {
        q: "Do we need to be on CheckPlus to list?",
        a:
          "No. We set up an account for you when you list, because Stripe verification runs through " +
          "it — and your Plug login works there too, so there is no second password to manage. " +
          "Switch on receipts, reports, your donation page and campaigns whenever you want.",
      },
      {
        q: "Do we see who our supporters are?",
        a:
          "Businesses always, with the rule that sent the money. Individuals appear by name where " +
          "they have allowed it, and anonymously where they have not — either way the amounts and " +
          "dates are itemised.",
      },
      {
        q: "What are unit costs and do we have to publish them?",
        a:
          "They are the plain-language prices of your work — what HK$120 or HK$800 buys. They are " +
          "required, because they are what turns a giver's total into a sentence they can believe.",
      },
    ],
  },

  /* Footer "on this page" links; keys match PAGE_SECTIONS in data.js. */
  sectionLabels: {
    flow: "How it connects",
    sides: "What each side gets",
    values: "Why Plug",
    how: "How it works",
    calculator: "What your giving buys",
    causes: "The marketplace",
    impact: "Impact",
    rules: "Choose your rule",
    customers: "What customers see",
    directory: "In good company",
    why: "Why list on Plug",
    dashboard: "Your dashboard",
    checkplus: "CheckPlus integration",
    join: "How to join",
    faq: "Questions",
  },

  faq: {
    title: "Questions people ask",
    lede: "Anything else, feel free to contact the Plug team.",
    contact: "Contact support →",
  },

  cta: {
    about: {
      headline: "Whichever side you are on, it starts with one account.",
      sub:
        "Businesses, nonprofits and donors sign up in the same place — we'll ask which you are " +
        "once, then set up the right dashboard for you.",
      primary: "Create a free account",
      secondary: "Browse nonprofits",
    },
    donors: {
      headline: "Start with HK$100 a month. Your first receipt lands the same day.",
      sub: "Individuals, businesses and nonprofits all sign up in the same place — we'll ask which you are once.",
      primary: "Create a free account",
      secondary: "Browse nonprofits",
    },
    business: {
      headline: "Publish a rule this week. Report on it all year.",
      sub:
        "We verify your Business Registration, you pick the nonprofits, and the giving runs off " +
        "your Stripe settlements from the next cycle.",
      primary: "Publish a giving rule",
      secondary: "Talk to our team",
    },
    nonprofit: {
      headline: "List your organisation and stop chasing donors.",
      sub:
        "Free to list, nothing monthly, and one transfer a month with a statement your auditor " +
        "accepts as-is.",
      primary: "Apply to list",
      secondary: "Check eligibility",
    },
  },

  footer: {
    blurb: "A Social Impact Ecosystem for Hong Kong.",
    onThisPageHead: "ON THIS PAGE",
    whoHead: "WHO IT'S FOR",
    whoAbout: "What is Plug",
    whoDonors: "Donors",
    whoBusiness: "Businesses",
    whoNonprofit: "Nonprofits",
    whoReports: "Impact reports",
    trustHead: "TRUST",
    trustSecurity: "Security",
    trustStripe: "Stripe partnership",
    trustSection88: "Section 88 verification",
    trustPrivacy: "Privacy",
    companyHead: "COMPANY",
    companyAbout: "About",
    companyContact: "Contact",
    companySupport: "Support Hong Kong",
    companyTerms: "Terms",
    note: "© 2026 Plug. Nonprofits must be registered in Hong Kong under Section 88 to receive funds.",
    region: "Hong Kong · HKD",
  },
};

export default copyEn;
